import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface MarqueeProps {
  children: ReactNode;
  /** Seconds for one full lap. */
  duration?: number;
  /** Reverse direction. */
  reverse?: boolean;
  className?: string;
  /** Pauses on hover. */
  pauseOnHover?: boolean;
  /** Gap between items in tailwind classes (e.g. "gap-12"). */
  gapClassName?: string;
}

/**
 * Endless horizontal marquee. Duplicates children once so the loop is
 * seamless. Caller is responsible for laying children out horizontally
 * (e.g. with `flex` + `shrink-0`).
 */
export function Marquee({
  children,
  duration = 36,
  reverse = false,
  className,
  pauseOnHover = false,
  gapClassName = "gap-12",
}: MarqueeProps) {
  const reduce = useReducedMotion();
  const x = reverse ? ["0%", "-50%"].reverse() : ["0%", "-50%"];
  return (
    <div
      className={cn(
        "group relative w-full overflow-hidden",
        // Fade the edges so logos blend into the background.
        "[mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]",
        className,
      )}
    >
      <motion.div
        className={cn("flex w-max shrink-0", gapClassName)}
        animate={reduce ? undefined : { x }}
        transition={
          reduce
            ? undefined
            : { duration, ease: "linear", repeat: Infinity }
        }
        style={
          pauseOnHover
            ? { animationPlayState: "running" }
            : undefined
        }
        whileHover={pauseOnHover ? { transition: { duration: 0 } } : undefined}
      >
        {children}
        {children}
      </motion.div>
    </div>
  );
}
